/**
 * Policy Autorepair Page (F072)
 *
 * Converts LeftShield from blocker to fixer. Shows repair jobs with
 * full lifecycle: detect → generate → approve → execute → verify.
 *
 * Route: /autorepair | Nav: Operations > Autorepair
 */
import React, { useState } from 'react';
import {
  Title2,
  Body1,
  Body2,
  Caption1,
  Card,
  CardHeader,
  Badge,
  Button,
  tokens,
  Divider,
  Spinner,
  Tooltip,
} from '@fluentui/react-components';
import {
  WrenchRegular,
  CheckmarkCircleRegular,
  DismissCircleRegular,
  PlayRegular,
  ArrowUndoRegular,
  DocumentCheckmarkRegular,
  ShieldCheckmarkRegular,
  BotRegular,
  PersonRegular,
  PeopleRegular,
  LinkRegular,
} from '@fluentui/react-icons';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { usePageStyles } from '../styles/page-styles';
import { PageHeader } from '../components/common/PageHeader';
import { EmptyStateCard } from '../components/common/EmptyStateCard';
import { governanceTokens } from '../components/design-system';
import { autorepairApi, type RepairJob } from '../services/api';

// ── Status + Mode mappings ────────────────────────────────────

type JobStatus = 'detected' | 'suggested' | 'awaiting_approval' | 'executing' | 'verifying' | 'resolved' | 'failed' | 'cancelled';

const statusLabels: Record<string, string> = {
  detected: 'Detected',
  suggested: 'Fix Ready',
  awaiting_approval: 'Approved',
  executing: 'Executing',
  verifying: 'Verifying',
  resolved: 'Resolved',
  failed: 'Failed',
  cancelled: 'Cancelled',
};

const statusColors: Record<string, 'warning' | 'success' | 'informative' | 'danger' | 'important'> = {
  detected: 'informative',
  suggested: 'warning',
  awaiting_approval: 'informative',
  executing: 'important',
  verifying: 'informative',
  resolved: 'success',
  failed: 'danger',
  cancelled: 'informative',
};

const severityColors: Record<string, 'danger' | 'important' | 'warning' | 'informative'> = {
  critical: 'danger',
  high: 'important',
  medium: 'warning',
  low: 'informative',
};

const modeIcons: Record<string, React.ReactElement> = {
  auto: <BotRegular />,
  assisted: <PeopleRegular />,
  manual: <PersonRegular />,
};

const modeLabels: Record<string, string> = {
  auto: 'Auto',
  assisted: 'Assisted',
  manual: 'Manual',
};

export const AutorepairPage: React.FC = () => {
  const ps = usePageStyles();
  const queryClient = useQueryClient();
  const [selectedId, setSelectedId] = useState<string | null>(null);

  // ── Data fetching ──────────────────────────────────────────
  const { data, isLoading: jobsLoading } = useQuery({
    queryKey: ['autorepair', 'jobs'],
    queryFn: () => autorepairApi.getJobs(),
  });

  const jobs = data?.items ?? [];
  const selected = jobs.find(j => j.id === selectedId) ?? null;

  const pendingCount = jobs.filter(j => j.status === 'detected' || j.status === 'suggested').length;
  const resolvedCount = jobs.filter(j => j.status === 'resolved').length;
  const failedCount = jobs.filter(j => j.status === 'failed').length;

  // ── Mutations ──────────────────────────────────────────────
  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['autorepair'] });

  const generateFix = useMutation({
    mutationFn: (id: string) => autorepairApi.generateFix(id),
    onSuccess: invalidate,
  });

  const approve = useMutation({
    mutationFn: (id: string) => autorepairApi.approveFix(id),
    onSuccess: invalidate,
  });

  const reject = useMutation({
    mutationFn: (id: string) => autorepairApi.rejectFix(id),
    onSuccess: invalidate,
  });

  const execute = useMutation({
    mutationFn: (id: string) => autorepairApi.executeFix(id),
    onSuccess: invalidate,
  });

  const verify = useMutation({
    mutationFn: (id: string) => autorepairApi.verifyFix(id),
    onSuccess: invalidate,
  });

  const rollback = useMutation({
    mutationFn: (id: string) => autorepairApi.rollback(id),
    onSuccess: invalidate,
  });

  const manualResolve = useMutation({
    mutationFn: (id: string) => autorepairApi.manualResolve(id),
    onSuccess: invalidate,
  });

  // ── Render helpers ─────────────────────────────────────────
  const renderModeBadge = (mode: string) => (
    <Tooltip content={`Repair mode: ${modeLabels[mode] ?? mode}`} relationship="label">
      <Badge
        appearance="tint"
        color={mode === 'auto' ? 'success' : mode === 'assisted' ? 'informative' : 'warning'}
        size="small"
        icon={modeIcons[mode]}
      >
        {modeLabels[mode] ?? mode}
      </Badge>
    </Tooltip>
  );

  const renderActions = (job: RepairJob) => {
    const s = job.status as JobStatus;
    const buttons: React.ReactElement[] = [];

    if (s === 'detected') {
      buttons.push(
        <Button
          key="generate"
          appearance="primary"
          size="small"
          icon={generateFix.isPending ? <Spinner size="tiny" /> : <WrenchRegular />}
          disabled={generateFix.isPending}
          onClick={() => generateFix.mutate(job.id)}
        >
          Generate Fix
        </Button>
      );
      buttons.push(
        <Button
          key="manual"
          appearance="outline"
          size="small"
          icon={<DocumentCheckmarkRegular />}
          onClick={() => manualResolve.mutate(job.id)}
        >
          Mark Resolved
        </Button>
      );
    }

    if (s === 'suggested') {
      buttons.push(
        <Button
          key="approve"
          appearance="primary"
          size="small"
          icon={approve.isPending ? <Spinner size="tiny" /> : <CheckmarkCircleRegular />}
          disabled={approve.isPending}
          onClick={() => approve.mutate(job.id)}
        >
          Approve
        </Button>
      );
      buttons.push(
        <Button
          key="reject"
          appearance="outline"
          size="small"
          icon={<DismissCircleRegular />}
          onClick={() => reject.mutate(job.id)}
        >
          Reject
        </Button>
      );
    }

    if (s === 'awaiting_approval') {
      buttons.push(
        <Button
          key="execute"
          appearance="primary"
          size="small"
          icon={execute.isPending ? <Spinner size="tiny" /> : <PlayRegular />}
          disabled={execute.isPending}
          onClick={() => execute.mutate(job.id)}
        >
          Execute Fix
        </Button>
      );
    }

    if (s === 'verifying') {
      buttons.push(
        <Button
          key="verify"
          appearance="primary"
          size="small"
          icon={verify.isPending ? <Spinner size="tiny" /> : <ShieldCheckmarkRegular />}
          disabled={verify.isPending}
          onClick={() => verify.mutate(job.id)}
        >
          Confirm Verified
        </Button>
      );
      buttons.push(
        <Button
          key="rollback"
          appearance="outline"
          size="small"
          icon={<ArrowUndoRegular />}
          onClick={() => rollback.mutate(job.id)}
        >
          Rollback
        </Button>
      );
    }

    if (s === 'failed') {
      buttons.push(
        <Button
          key="rollback"
          appearance="outline"
          size="small"
          icon={<ArrowUndoRegular />}
          onClick={() => rollback.mutate(job.id)}
        >
          Rollback
        </Button>
      );
      buttons.push(
        <Button
          key="retry"
          appearance="primary"
          size="small"
          icon={<WrenchRegular />}
          onClick={() => generateFix.mutate(job.id)}
        >
          Retry
        </Button>
      );
    }

    return buttons.length > 0 ? (
      <div style={{ display: 'flex', gap: tokens.spacingHorizontalM, flexWrap: 'wrap' }}>{buttons}</div>
    ) : null;
  };

  const renderGeneratedFix = (fix: Record<string, unknown>) => {
    const diff = fix.diff as string | undefined;
    const summary = fix.summary as string | undefined;
    const type = fix.type as string | undefined;

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalS }}>
        <Body2 style={{ fontWeight: 600 }}>Generated Fix</Body2>
        {type && <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>Type: {type}</Caption1>}
        {summary && <Body2>{summary}</Body2>}
        {diff && (
          <pre style={{
            backgroundColor: tokens.colorNeutralBackground3,
            padding: tokens.spacingVerticalM,
            borderRadius: tokens.borderRadiusMedium,
            fontSize: '12px',
            fontFamily: 'Consolas, monospace',
            overflow: 'auto',
            maxHeight: '200px',
            whiteSpace: 'pre-wrap',
            margin: 0,
          }}>
            {diff}
          </pre>
        )}
      </div>
    );
  };

  const renderExecutionResult = (result: Record<string, unknown>) => {
    const success = result.success as boolean;
    const output = result.output as string | undefined;
    const prUrl = result.pullRequestUrl as string | undefined;

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalS }}>
        <Body2 style={{ fontWeight: 600 }}>Execution Result</Body2>
        <Badge appearance="filled" color={success ? 'success' : 'danger'} size="small">
          {success ? 'Success' : 'Failed'}
        </Badge>
        {output && <Body2>{output}</Body2>}
        {prUrl && (
          <Body2>
            <LinkRegular style={{ marginRight: 4 }} />
            <a href={prUrl} target="_blank" rel="noopener noreferrer" style={{ color: tokens.colorBrandForeground1 }}>
              View Pull Request
            </a>
          </Body2>
        )}
      </div>
    );
  };

  const renderVerification = (result: Record<string, unknown>) => {
    const passed = result.passed as boolean;
    const details = result.details as string | undefined;
    const checks = result.checksRun as string[] | undefined;

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalS }}>
        <Body2 style={{ fontWeight: 600 }}>Verification</Body2>
        <Badge appearance="filled" color={passed ? 'success' : 'danger'} size="small">
          {passed ? 'Passed' : 'Failed'}
        </Badge>
        {details && <Body2>{details}</Body2>}
        {checks && checks.length > 0 && (
          <div style={{ display: 'flex', gap: tokens.spacingHorizontalXS, flexWrap: 'wrap' }}>
            {checks.map((c, i) => <Badge key={i} appearance="outline" size="small">{c}</Badge>)}
          </div>
        )}
      </div>
    );
  };

  // ── Main render ────────────────────────────────────────────

  if (jobsLoading) {
    return (
      <div className={ps.page}>
        <PageHeader
          title="Automated Actions"
          subtitle="Autonomous remediation executing in your delivery pipeline"
          icon={<WrenchRegular />}
          iconColor={governanceTokens.colorGovernancePrimary}
        />
        <div style={{ display: 'flex', justifyContent: 'center', padding: tokens.spacingVerticalXXXL }}>
          <Spinner label="Loading repair jobs..." />
        </div>
      </div>
    );
  }

  return (
    <div className={ps.page}>
      <PageHeader
        title="Automated Actions"
        subtitle="Autonomous remediation executing in your delivery pipeline"
        icon={<WrenchRegular />}
        iconColor={governanceTokens.colorGovernancePrimary}
      />

      {/* Summary */}
      <div className={ps.metricGrid}>
        <Card>
          <div className={ps.cardBody}>
            <div className={ps.statTile}>
              <Body2 style={{ color: tokens.colorNeutralForeground3 }}>Pending</Body2>
              <Title2 style={{ color: pendingCount > 0 ? governanceTokens.colorStatusWarning : undefined }}>{pendingCount}</Title2>
            </div>
          </div>
        </Card>
        <Card>
          <div className={ps.cardBody}>
            <div className={ps.statTile}>
              <Body2 style={{ color: tokens.colorNeutralForeground3 }}>Resolved</Body2>
              <Title2 style={{ color: governanceTokens.colorStatusSuccess }}>{resolvedCount}</Title2>
            </div>
          </div>
        </Card>
        <Card>
          <div className={ps.cardBody}>
            <div className={ps.statTile}>
              <Body2 style={{ color: tokens.colorNeutralForeground3 }}>Failed</Body2>
              <Title2 style={{ color: failedCount > 0 ? '#E85252' : undefined }}>{failedCount}</Title2>
            </div>
          </div>
        </Card>
        <Card>
          <div className={ps.cardBody}>
            <div className={ps.statTile}>
              <Body2 style={{ color: tokens.colorNeutralForeground3 }}>Total Jobs</Body2>
              <Title2>{jobs.length}</Title2>
            </div>
          </div>
        </Card>
      </div>

      <div className={ps.twoColumn}>
        {/* Job List */}
        <Card>
          <CardHeader header={<Title2>Repair Jobs</Title2>} />
          <div className={ps.cardBody}>
            {jobs.length > 0 ? (
              <div className={ps.rowList}>
                {jobs.map(j => (
                  <div
                    key={j.id}
                    className={ps.rowItem}
                    style={{
                      cursor: 'pointer',
                      backgroundColor: selectedId === j.id ? tokens.colorNeutralBackground3 : undefined,
                    }}
                    onClick={() => setSelectedId(j.id)}
                  >
                    <div style={{ display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalXXS, flex: 1 }}>
                      <Body1 style={{ fontWeight: 500 }}>{j.title}</Body1>
                      <div style={{ display: 'flex', gap: tokens.spacingHorizontalS, flexWrap: 'wrap', alignItems: 'center' }}>
                        {renderModeBadge(j.repairMode)}
                        <Badge appearance="tint" color={severityColors[j.severity] ?? 'informative'} size="small">
                          {j.severity}
                        </Badge>
                        <Badge appearance="filled" color={statusColors[j.status] ?? 'informative'} size="small">
                          {statusLabels[j.status] ?? j.status}
                        </Badge>
                        {j.confidence > 0 && (
                          <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>
                            {Math.round(j.confidence * 100)}% confidence
                          </Caption1>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <EmptyStateCard
                title="No Active Remediation Jobs"
                description="System Status: Autonomous remediation active. No governance violations currently require automated intervention. Recent: No automated actions in the last 24 hours."
                icon={<WrenchRegular />}
              />
            )}
          </div>
        </Card>

        {/* Detail */}
        <Card>
          <CardHeader header={<Title2>{selected ? 'Job Details' : 'Select a Job'}</Title2>} />
          <div className={ps.cardBody}>
            {selected ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalM }}>
                <Body1 style={{ fontWeight: 600 }}>{selected.title}</Body1>
                <Body2>{selected.description}</Body2>

                {/* Badges row */}
                <div style={{ display: 'flex', gap: tokens.spacingHorizontalS, flexWrap: 'wrap' }}>
                  {renderModeBadge(selected.repairMode)}
                  <Badge appearance="tint" color={severityColors[selected.severity] ?? 'informative'}>
                    {selected.severity}
                  </Badge>
                  <Badge appearance="filled" color={statusColors[selected.status] ?? 'informative'}>
                    {statusLabels[selected.status] ?? selected.status}
                  </Badge>
                </div>

                {/* Governance context */}
                {(selected.frameworkRefs?.length || selected.policyId || selected.controlId) && (
                  <div style={{ display: 'flex', gap: tokens.spacingHorizontalS, flexWrap: 'wrap' }}>
                    {selected.frameworkRefs?.map(f => (
                      <Badge key={f} appearance="outline" size="small">Framework: {f}</Badge>
                    ))}
                    {selected.policyId && <Badge appearance="outline" size="small">Policy: {selected.policyId}</Badge>}
                    {selected.controlId && <Badge appearance="outline" size="small">Control: {selected.controlId}</Badge>}
                  </div>
                )}

                {selected.confidence > 0 && (
                  <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>
                    AI Confidence: {Math.round(selected.confidence * 100)}%
                  </Caption1>
                )}

                <Divider />

                {/* Generated fix */}
                {selected.generatedFix && renderGeneratedFix(selected.generatedFix as Record<string, unknown>)}

                {/* Execution result */}
                {selected.executionResult && (
                  <>
                    <Divider />
                    {renderExecutionResult(selected.executionResult as Record<string, unknown>)}
                  </>
                )}

                {/* Verification */}
                {selected.verificationResult && (
                  <>
                    <Divider />
                    {renderVerification(selected.verificationResult as Record<string, unknown>)}
                  </>
                )}

                {/* Evidence link */}
                {selected.evidenceRecordId && (
                  <>
                    <Divider />
                    <Body2>
                      <DocumentCheckmarkRegular style={{ marginRight: 4 }} />
                      Evidence Record: <strong>{selected.evidenceRecordId}</strong>
                    </Body2>
                  </>
                )}

                <Divider />

                {/* Action buttons */}
                {renderActions(selected)}
              </div>
            ) : (
              <Body1 className={ps.emptyState}>
                Select a repair job to view details and take action.
              </Body1>
            )}
          </div>
        </Card>
      </div>
    </div>
  );
};
